// ─────────────────────────────────────────────────────────────────────
// THE PASSKEY PROBE — what THIS context can do with WebAuthn (#676).
//
// A measurement, not a feature. The question #676 asks is whether the native
// member app's WebView can run a passkey ceremony at all, and it can only be
// answered from INSIDE that WebView: Samsung Internet passing on the same phone
// (2026-10-08) says nothing about it. So this reads what the context offers and
// runs one ceremony on request, and the page shows the raw result for the owner
// to copy onto the issue.
//
// IT TALKS TO NO SERVER. The challenge is random and local, nothing is verified
// and nothing is stored — what is measured is whether the platform lets the
// ceremony run and what it reports, not whether a credential is accepted. That
// is also why it is safe to leave reachable: it carries no authority, reads no
// account, and every effect stays on the device that pressed the button.
//
// ONE EFFECT IS REAL, and the page says so: «create» asks the device to make a
// passkey for this host, with a throwaway user. On a phone that syncs passkeys
// it lands in the password manager, labelled as the probe, until removed.
//
// Everything that touches the browser takes it as an argument (`ProbeHost`),
// so the tests drive the same code with a scripted WebAuthn implementation.
// ─────────────────────────────────────────────────────────────────────

/** One read: a value, «the API is not there», or what it threw. */
export type Probe<T> =
  | { kind: 'value'; value: T }
  | { kind: 'absent' }
  | { kind: 'threw'; name: string; message: string };

type StaticPkc = {
  isUserVerifyingPlatformAuthenticatorAvailable?: () => Promise<boolean>;
  isConditionalMediationAvailable?: () => Promise<boolean>;
  getClientCapabilities?: () => Promise<Record<string, boolean>>;
};

type UaData = {
  getHighEntropyValues?: (hints: string[]) => Promise<Record<string, unknown>>;
};

/** The slice of `window` / `navigator` the probe reads. */
export interface ProbeHost {
  location: { origin: string; hostname: string };
  PublicKeyCredential?: unknown;
  navigator: {
    userAgent: string;
    userAgentData?: UaData;
    credentials?: {
      create?: (options: CredentialCreationOptions) => Promise<Credential | null>;
      get?: (options: CredentialRequestOptions) => Promise<Credential | null>;
    };
  };
  crypto: { getRandomValues: <T extends ArrayBufferView>(array: T) => T };
  now: () => number;
}

export function browserProbeHost(): ProbeHost {
  return {
    location: window.location,
    PublicKeyCredential: (window as { PublicKeyCredential?: unknown }).PublicKeyCredential,
    navigator: window.navigator as ProbeHost['navigator'],
    crypto: window.crypto,
    now: () => performance.now(),
  };
}

function errorProbe(e: unknown): { kind: 'threw'; name: string; message: string } {
  const err = e as { name?: unknown; message?: unknown } | null;
  return {
    kind: 'threw',
    name: typeof err?.name === 'string' ? err.name : 'Error',
    message: typeof err?.message === 'string' ? err.message : String(e),
  };
}

async function read<T>(fn: (() => Promise<T>) | undefined): Promise<Probe<T>> {
  if (typeof fn !== 'function') return { kind: 'absent' };
  try {
    return { kind: 'value', value: await fn() };
  } catch (e) {
    return errorProbe(e);
  }
}

// ── What the context offers ──────────────────────────────────────────

export interface CapabilityReport {
  /** `typeof window.PublicKeyCredential` — `'undefined'` means no WebAuthn at all. */
  publicKeyCredential: string;
  credentialsCreate: boolean;
  credentialsGet: boolean;
  platformAuthenticator: Probe<boolean>;
  conditionalMediation: Probe<boolean>;
  /** WebAuthn L3 `getClientCapabilities()`, where the engine has it. */
  clientCapabilities: Probe<Record<string, boolean>>;
}

export async function readCapabilities(host: ProbeHost): Promise<CapabilityReport> {
  const pkc = host.PublicKeyCredential as StaticPkc | undefined;
  const bound = <K extends keyof StaticPkc>(k: K) =>
    pkc && typeof pkc[k] === 'function'
      ? (pkc[k] as () => Promise<never>).bind(pkc as object)
      : undefined;
  return {
    publicKeyCredential: typeof host.PublicKeyCredential,
    credentialsCreate: typeof host.navigator.credentials?.create === 'function',
    credentialsGet: typeof host.navigator.credentials?.get === 'function',
    platformAuthenticator: await read<boolean>(
      bound('isUserVerifyingPlatformAuthenticatorAvailable'),
    ),
    conditionalMediation: await read<boolean>(bound('isConditionalMediationAvailable')),
    clientCapabilities: await read<Record<string, boolean>>(bound('getClientCapabilities')),
  };
}

// ── Which WebView, which OS ──────────────────────────────────────────

export interface UserAgentFacts {
  /** Android System WebView marks itself with `; wv)` in the UA. */
  androidWebView: boolean;
  /** `Chrome/x.y.z.w` — for a WebView, the System WebView's version. */
  chromiumVersion: string | null;
  androidVersion: string | null;
  /** The shell appends `StarpointApp/<version>` (apps/mobile WebShell). */
  starpointApp: string | null;
}

export function parseUserAgent(ua: string): UserAgentFacts {
  return {
    androidWebView: /;\s*wv\)/.test(ua),
    chromiumVersion: /\bChrome\/([\d.]+)/.exec(ua)?.[1] ?? null,
    androidVersion: /\bAndroid ([\d.]+)/.exec(ua)?.[1] ?? null,
    starpointApp: /\bStarpointApp\/([\w.-]+)/.exec(ua)?.[1] ?? null,
  };
}

export interface EnvironmentReport extends UserAgentFacts {
  origin: string;
  hostname: string;
  userAgent: string;
  /** UA-CH high-entropy values: the full WebView version list, OS version, model. */
  highEntropy: Probe<Record<string, unknown>>;
}

export async function readEnvironment(host: ProbeHost): Promise<EnvironmentReport> {
  const uaData = host.navigator.userAgentData;
  const getHigh = uaData?.getHighEntropyValues?.bind(uaData);
  return {
    origin: host.location.origin,
    hostname: host.location.hostname,
    userAgent: host.navigator.userAgent,
    ...parseUserAgent(host.navigator.userAgent),
    highEntropy: await read(
      getHigh ? () => getHigh(['fullVersionList', 'platformVersion', 'model']) : undefined,
    ),
  };
}

// ── Which RP IDs to try ──────────────────────────────────────────────

/**
 * The host itself, and its parent domain when it has one.
 *
 * On dev that is `dev.spoint.no` (the host this web app serves, and the one
 * whose assetlinks.json is ours) and `spoint.no` (the RP ID the API is
 * configured with, served elsewhere). Trying both separates «the WebView cannot
 * do WebAuthn» from «the apex does not vouch for the app yet» — which otherwise
 * produce the same failure. Deliberately not a public-suffix list: the browser
 * is the authority on what is registrable, and it says so in the error.
 */
export function rpIdCandidates(hostname: string): string[] {
  const host = hostname.trim().toLowerCase();
  if (!host) return [];
  if (/^[\d.]+$/.test(host) || host.includes(':')) return [host]; // an IP address
  const labels = host.split('.');
  return labels.length >= 3 ? [host, labels.slice(1).join('.')] : [host];
}

// ── One ceremony ─────────────────────────────────────────────────────

export type CeremonyKind = 'get' | 'create';

export interface CeremonyReport {
  ceremony: CeremonyKind;
  rpId: string;
  outcome: 'completed' | 'error' | 'unavailable';
  errorName?: string;
  errorMessage?: string;
  /**
   * How long the call took. An error in a few milliseconds means no system
   * sheet was shown; several seconds usually means one was, and was dismissed.
   */
  elapsedMs: number;
  /** From `clientDataJSON` on success — the ORIGIN the platform signed is the finding. */
  clientData?: { type: unknown; origin: unknown; crossOrigin?: unknown };
  authenticatorAttachment?: string | null;
}

/** The throwaway account a probe registration is made for. */
export const PROBE_USER = { name: 'webview-probe', displayName: 'Starpoint WebView probe' };

function challenge(host: ProbeHost, size: number): Uint8Array {
  return host.crypto.getRandomValues(new Uint8Array(size));
}

/** Pure: the options each ceremony runs with. */
export function ceremonyOptions(
  kind: CeremonyKind,
  rpId: string,
  host: ProbeHost,
): CredentialCreationOptions | CredentialRequestOptions {
  if (kind === 'get') {
    return {
      publicKey: {
        challenge: challenge(host, 32) as BufferSource,
        rpId,
        userVerification: 'required',
        allowCredentials: [],
        timeout: 60_000,
      },
    };
  }
  return {
    publicKey: {
      challenge: challenge(host, 32) as BufferSource,
      rp: { id: rpId, name: PROBE_USER.displayName },
      user: {
        id: challenge(host, 16) as BufferSource,
        name: PROBE_USER.name,
        displayName: PROBE_USER.displayName,
      },
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },
        { type: 'public-key', alg: -257 },
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        residentKey: 'required',
        userVerification: 'required',
      },
      attestation: 'none',
      timeout: 60_000,
    },
  };
}

/** Pure: the `type` and `origin` a credential's clientDataJSON carries, or undefined. */
export function readClientData(credential: unknown): CeremonyReport['clientData'] | undefined {
  const raw = (credential as { response?: { clientDataJSON?: unknown } } | null)?.response
    ?.clientDataJSON;
  // Tagged rather than `instanceof`: the buffer may come from another realm
  // (a WebView bridge, a test environment), where `instanceof` is false.
  const bytes = ArrayBuffer.isView(raw)
    ? new Uint8Array(raw.buffer, raw.byteOffset, raw.byteLength)
    : Object.prototype.toString.call(raw) === '[object ArrayBuffer]'
      ? new Uint8Array(raw as ArrayBuffer)
      : null;
  if (!bytes) return undefined;
  try {
    const json = JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
    return { type: json.type, origin: json.origin, crossOrigin: json.crossOrigin };
  } catch {
    return undefined;
  }
}

export async function runCeremony(
  kind: CeremonyKind,
  rpId: string,
  host: ProbeHost,
): Promise<CeremonyReport> {
  const fn = host.navigator.credentials?.[kind];
  if (typeof fn !== 'function' || typeof host.PublicKeyCredential === 'undefined') {
    return { ceremony: kind, rpId, outcome: 'unavailable', elapsedMs: 0 };
  }
  const started = host.now();
  const elapsed = () => Math.round(host.now() - started);
  try {
    const options = ceremonyOptions(kind, rpId, host);
    const credential = await (fn as (o: typeof options) => Promise<Credential | null>).call(
      host.navigator.credentials,
      options,
    );
    const attachment = (credential as { authenticatorAttachment?: unknown } | null)
      ?.authenticatorAttachment;
    return {
      ceremony: kind,
      rpId,
      outcome: 'completed',
      elapsedMs: elapsed(),
      clientData: readClientData(credential),
      authenticatorAttachment: typeof attachment === 'string' ? attachment : null,
    };
  } catch (e) {
    const { name, message } = errorProbe(e);
    return {
      ceremony: kind,
      rpId,
      outcome: 'error',
      errorName: name,
      errorMessage: message,
      elapsedMs: elapsed(),
    };
  }
}

/**
 * Pure: which reading-guide line applies. A HINT, not a verdict: the same
 * error name means different things depending on whether a system sheet
 * appeared, which only the person holding the phone can see.
 */
export type CeremonyHint =
  | 'completed'
  | 'notAllowed'
  | 'notSupported'
  | 'security'
  | 'aborted'
  | 'invalidState'
  | 'unavailable'
  | 'other';

export function ceremonyHint(report: CeremonyReport): CeremonyHint {
  if (report.outcome === 'completed') return 'completed';
  if (report.outcome === 'unavailable') return 'unavailable';
  switch (report.errorName) {
    case 'NotAllowedError':
      return 'notAllowed';
    case 'NotSupportedError':
      return 'notSupported';
    case 'SecurityError':
      return 'security';
    case 'AbortError':
      return 'aborted';
    case 'InvalidStateError':
      return 'invalidState';
    default:
      return 'other';
  }
}

// ── What the owner copies onto the issue ─────────────────────────────

export interface ProbeReport {
  probe: 'starpoint-passkey-probe';
  version: 1;
  shell: unknown;
  environment: EnvironmentReport | null;
  capabilities: CapabilityReport | null;
  ceremonies: CeremonyReport[];
}

export function probeReport(parts: Omit<ProbeReport, 'probe' | 'version'>): ProbeReport {
  return { probe: 'starpoint-passkey-probe', version: 1, ...parts };
}
